import React from "react";
import { Typography, Box, Chip } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { format } from "date-fns";
import {
  spacing,
  colors,
  typography,
} from "../../../../../../../../../../assets/styles/tokens";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import Collapse from "@mui/material/Collapse";
import Avatar from "@mui/material/Avatar";
import { Stack } from "@mui/system";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  selectUserData,
  selectUserToken,
} from "../../../../../../../../../../features/userSlice";
import { TrendsDataSetQueryIds } from "../../../../../../helpers/funcs/trendsAllQueryIDs";
import { useTrendsMobileOverride } from "../../../../../helpers/hooks/useTrendsMobileOverride";
import OnlyMobgraphSkel from "../../../../../helpers/components/OnlyMobgraphSkel";
import {
  CustomDot,
  CustomXTick,
} from "../../../../../helpers/components/GraphDesignEl";
import {
  setCpapvMobileDataRedux,
  setCpapvMobileLoading,
} from "../../../../../../../../../../features/trendsSlice";
import { getYAxisConfig } from "../../../../../helpers/funcs/getYAxisConfig";

export default function CPAPlayerValueChart() {
  const [searchParams] = useSearchParams();
  const [activeNote, setActiveNote] = React.useState<number | null>(null);
  const [expandedNotes] = React.useState<Set<number>>(new Set());
  const response = useSelector(selectUserData);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dateType = searchParams.get("dateType") || "click_date";
  const isClickQuery = dateType === "click_date";
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const startDate = searchParams.get("startDateTM");
  const endDate = searchParams.get("endDateTM");
  const websiteSharedUids = searchParams.get("workspace");
  const timeUnit = searchParams.get("timeUnitCpapvMobile");
  const chartRef = React.useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = React.useState(0);
  const seriesColor = colors.Brand[600].$value;
  const trendsMobileData = useSelector(
    (state: any) => state.trends.trendsMobileData,
  );
  const activityNotesDataTrendsMobile = useSelector(
    (state: any) => state.trends.activityNotesDataTrendsMobile,
  );
  const notesLabelFilter = useSelector(
    (state: any) => state.trends.notesLabelFilter,
  );
  const mobileDataRedux = useSelector(
    (state: any) => state.trends.cpapvMobileDataRedux,
  );
  const loading = useSelector((state: any) => state.trends.cpapvMobileLoading);

  const selectedDimensions = React.useMemo(() => {
    const uidList = websiteSharedUids?.split(",") ?? [];
    return (
      response?.data?.website_permissions?.flatMap((siteObj: any) => {
        const shared_uid = Object.keys(siteObj)[0];
        if (!uidList.includes(shared_uid)) return [];
        return (siteObj[shared_uid].dimensions ?? []).map(
          (dim: any) => dim.dimension_name,
        );
      }) ?? []
    );
  }, [response, websiteSharedUids]);

  const isOverride = !!searchParams.get("timeUnitCpapvMobile");

  const sourceData = isOverride
    ? mobileDataRedux.query_output
    : trendsMobileData?.query_output;
  const trendsData = React.useMemo(() => {
    return sourceData || [];
  }, [sourceData]);

  const filters = useSelector((state: any) => {
    const f = state.trends.mobileFilters;
    return {
      trafficSource: f?.trafficSource?.value || "all",
      country: f?.country?.value || "all",
      campaign: f?.campaign?.value || "1",
      adGroup: f?.adGroup?.value || "1",
      keyword: f?.keyword?.value || "1",
    };
  });

  const filteredNotes = React.useMemo(() => {
    if (!Array.isArray(activityNotesDataTrendsMobile)) return [];
    if (!notesLabelFilter) return activityNotesDataTrendsMobile;
    return activityNotesDataTrendsMobile.filter(
      (note: any) =>
        Array.isArray(note?.labels) && note.labels.includes(notesLabelFilter),
    );
  }, [activityNotesDataTrendsMobile, notesLabelFilter]);

  // const toggleFullNote = (index: number) => {
  //   setExpandedNotes((prev) => {
  //     const next = new Set(prev);
  //     next.has(index) ? next.delete(index) : next.add(index);
  //     return next;
  //   });
  // };

  const getNoteLeft = (index: number) => {
    const marginLeft = 12;
    const marginRight = 24;
    const dynamicWidth =
      chartData.length > 7 ? chartData.length * 60 : chartWidth;
    const usableWidth = dynamicWidth - marginLeft - marginRight;
    if (chartData.length <= 1) {
      return marginLeft + usableWidth / 2;
    }
    const step = usableWidth / (chartData.length - 1);
    return marginLeft + step * index;
  };
  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    const resize = new ResizeObserver((entries) => {
      setChartWidth(entries[0].contentRect.width);
    });
    resize.observe(chartRef.current);
    return () => resize.disconnect();
  }, []);

  const notesMap = React.useMemo(() => {
    if (!Array.isArray(filteredNotes)) return {};
    return filteredNotes.reduce((acc: any, note: any) => {
      if (!note?.activity_date) return acc;
      const formattedDate = format(new Date(note.activity_date), "dd MMM");
      if (!acc[formattedDate]) {
        acc[formattedDate] = [];
      }
      acc[formattedDate].push(note);
      return acc;
    }, {});
  }, [filteredNotes]);

  const chartData = React.useMemo(() => {
    if (!Array.isArray(trendsData)) return [];
    const parsed = trendsData
      .map((item: any) => {
        if (!item?.time_unit) return null;
        const date = new Date(item.time_unit);
        const rawMetric = item?.cpa_pv;
        if (typeof rawMetric !== "number" || isNaN(rawMetric)) return null;
        const metric = parseFloat(rawMetric.toFixed(2));
        if (isNaN(metric)) return null;
        return {
          x: format(date, "dd MMM"),
          y: metric,
          rawDate: date,
        };
      })
      .filter(Boolean);
    parsed.sort((a: any, b: any) => {
      return a.rawDate.getTime() - b.rawDate.getTime();
    });
    return parsed;
  }, [trendsData]);

  const yAxisConfig = React.useMemo(() => {
    return getYAxisConfig(chartData);
  }, [chartData]);

  const notesIndices = React.useMemo(() => {
    return chartData
      .map((item: any, index) => (notesMap[item.x] ? index : null))
      .filter((i) => i !== null);
  }, [chartData, notesMap]);
  const notes = notesIndices;

  const handleChartClick = (rechartsData: any) => {
    if (!rechartsData || rechartsData.activeTooltipIndex === undefined) {
      setActiveIndex(null);
      setActiveNote(null);
      return;
    }
    const index = rechartsData.activeTooltipIndex;
    const point = chartData[index];
    setActiveIndex((prev) => (prev === index ? null : index));
    if (point && notesMap[point.x]) {
      setActiveNote((prev) => (prev === index ? null : index));
    } else {
      setActiveNote(null);
    }
  };
  const activeNotes =
    activeNote !== null && chartData[activeNote]
      ? notesMap[chartData[activeNote].x]
      : null;

  const params = React.useMemo(() => {
    const baseParams = {
      oauthTokenHeader,
      queryId: isClickQuery
        ? TrendsDataSetQueryIds.CLICK
        : TrendsDataSetQueryIds.ACTION,
      startDate: startDate || "",
      endDate: endDate || "",
      timeUnit: timeUnit ? timeUnit : "day", // default
      type: "site",
      websiteSharedUids: websiteSharedUids ? websiteSharedUids.split(",") : [],
      dimensions: selectedDimensions?.[0] ? [selectedDimensions[0]] : [],
    };

    if (isClickQuery) {
      return {
        ...baseParams,
        trafficSource: filters.trafficSource,
        country: filters.country,
        campaignId: filters.campaign,
        adgroupId: filters.adGroup,
        kwId: filters.keyword,
        matchtype: "all",
      };
    }

    return baseParams;
    // eslint-disable-next-line
  }, [
    oauthTokenHeader,
    startDate,
    endDate,
    websiteSharedUids,
    selectedDimensions,
    filters,
  ]);

  const { fetchOverride } = useTrendsMobileOverride({
    params,
    setDataAction: setCpapvMobileDataRedux,
    setLoadingAction: setCpapvMobileLoading,
  });
  React.useEffect(() => {
    const timeUnit = searchParams.get("timeUnitCpapvMobile");
    if (!timeUnit) return;
    fetchOverride({
      ...params,
      timeUnit,
    });
    // eslint-disable-next-line
  }, [timeUnit]);
  if (loading) {
    return <OnlyMobgraphSkel />;
  }
  if (!chartData.length) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 242,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography
          color={colors.GrayLightMode[700].$value}
          sx={{
            fontSize: typography.Size.Text.sm.$value,
            lineHeight: typography.Line_height.text.sm.$value,
            fontWeight: typography.Font_weight.Medium.$value,
          }}
        >
          No Data
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Chart */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            width: 43,
            flexShrink: 0,
            position: "sticky",
            left: 0,
            zIndex: 2,
            backgroundColor: "#fff",
          }}
        >
          <ResponsiveContainer width="100%" height={242}>
            <AreaChart
              data={chartData}
              margin={{ top: 16, right: 0, left: 8, bottom: 24 }}
            >
              <XAxis
                dataKey="x"
                tick={false}
                tickLine={false}
                axisLine={false}
                interval={0}
                height={48}
              />
              <YAxis
                domain={yAxisConfig.domain}
                ticks={yAxisConfig.ticks}
                tick={{
                  fill: colors.GrayLightMode[600].$value,
                  fontSize: typography.Size.Text.xxs.$value,
                  fontWeight: typography.Font_weight.Medium.$value,
                }}
                tickLine={false}
                axisLine={false}
                width={35}
                tickMargin={3}
                tickFormatter={(value) =>
                  Intl.NumberFormat(undefined, {
                    notation: "compact",
                    maximumFractionDigits: 1,
                  }).format(value)
                }
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ flex: 1, overflowX: "auto", overflowY: "hidden" }}>
          <Box
            ref={chartRef}
            sx={{
              minWidth:
                chartData.length > 7 ? `${chartData.length * 60}px` : "100%",
              position: "relative",
            }}
          >
            <ResponsiveContainer width="100%" height={242}>
              <AreaChart
                data={chartData}
                margin={{ top: 16, right: 24, left: 10, bottom: 24 }}
                onClick={(data, event) => {
                  event?.stopPropagation();
                  handleChartClick(data);
                }}
                style={{ cursor: "pointer" }}
              >
                <defs>
                  <linearGradient
                    id="impressionGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor={seriesColor}
                      stopOpacity={0.5}
                    />
                    <stop
                      offset="100%"
                      stopColor={colors.Brand[50].$value}
                      stopOpacity={0.09}
                    />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  strokeDasharray=""
                  vertical={false}
                  stroke={colors.GrayLightMode[300].$value}
                  opacity={0.3}
                />

                <XAxis
                  dataKey="x"
                  tick={(tickProps) => (
                    <CustomXTick
                      {...tickProps}
                      activeNote={activeNote}
                      activeIndex={activeIndex}
                    /> // ← add activeIndex
                  )}
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                  height={48}
                  tickMargin={10}
                />

                <YAxis
                  domain={yAxisConfig.domain}
                  ticks={yAxisConfig.ticks}
                  hide
                />

                <Tooltip
                  contentStyle={{
                    borderRadius: spacing.Radius.xs.$value,
                    border: "none",
                    boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
                    fontSize: typography.Size.Text.xs.$value,
                    padding: "4px",
                    display: "flex",
                    fontWeight: typography.Font_weight.Bold.$value,
                  }}
                  cursor={{
                    stroke: colors.GrayLightMode[200].$value,
                    strokeWidth: 1,
                  }}
                  labelFormatter={() => ""}
                  formatter={(value) => [`${value}`]}
                  itemStyle={{ padding: 0 }}
                  separator=""
                />

                <Area
                  type="monotone"
                  dataKey="y"
                  stroke={seriesColor}
                  strokeWidth={1.5}
                  fill="url(#impressionGradient)"
                  dot={(dotProps: any) => (
                    <CustomDot
                      {...dotProps}
                      activeIndex={activeIndex}
                      color={seriesColor}
                    />
                  )}
                  activeDot={false}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>

            {/* Note Icons */}
            {notes.map((index) => (
              <IconButton
                key={index}
                size="small"
                sx={{
                  position: "absolute",
                  bottom: 0,
                  marginLeft: "-3px",
                  left: getNoteLeft(index),
                  transform: "translateX(-50%)",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // handleNoteClick(index);
                }}
                disableRipple
              >
                {activeNote === index ? (
                  <StickyNote2OutlinedIcon
                    sx={{
                      width: "14px",
                      color: colors.GrayLightMode[900].$value,
                    }}
                  />
                ) : (
                  <StickyNote2OutlinedIcon
                    sx={{
                      width: "14px",
                      fill: colors.GrayLightMode[600].$value,
                    }}
                  />
                )}
              </IconButton>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Collapse note panel */}
      <Collapse in={activeNote !== null}>
        {activeNotes?.map((note: any, i: number) => (
          <Box
            key={i}
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: "flex",
              px: 1.5,
              py: 1,
              gap: 0.5,
              alignItems: "flex-start",
              flexDirection: "column",
            }}
          >
            <Stack flexDirection="row" gap={0.5} alignItems="center">
              <Avatar sx={{ width: 18, height: 18 }} src="#" />
              <Typography
                fontSize={typography.Size.Text.xs.$value}
                lineHeight={typography.Line_height.text.xs.$value}
                fontWeight={typography.Font_weight.Regular.$value}
                color={colors.GrayLightMode[700].$value}
              >
                {note.created_by}
              </Typography>
              <Chip
                label={note.note_type}
                variant="outlined"
                size="small"
                sx={{
                  height: spacing.Spacing["1xl"].$value,
                  fontSize: typography.Size.Text.xxs.$value,
                  lineHeight: typography.Line_height.text.xxs.$value,
                  fontWeight: typography.Font_weight.Semibold.$value,
                  color: colors.Brand[700].$value,
                  borderColor: colors.Brand[700].$value,
                  borderRadius: spacing.Radius["2xl"].$value,
                  ml: 0.5,
                }}
              />
            </Stack>
            <Box>
              <Typography
                fontSize={typography.Size.Text.xs.$value}
                color={colors.GrayLightMode[900].$value}
                fontWeight={typography.Font_weight.Semibold.$value}
              >
                {note.note}
              </Typography>

              <Collapse in={expandedNotes.has(i)}>
                <Typography
                  fontSize={typography.Size.Text.xs.$value}
                  color={colors.GrayLightMode[900].$value}
                  fontWeight={typography.Font_weight.Semibold.$value}
                  mt={0.5}
                >
                  {note.details || "No additional details"}
                </Typography>
              </Collapse>

              <Typography
                fontSize={typography.Size.Text.xs.$value}
                fontWeight={typography.Font_weight.Medium.$value}
                color={colors.Brand[700].$value}
                mt={0.5}
                sx={{
                  cursor: "not-allowed",
                  opacity: 0.5,
                  pointerEvents: "none",
                }}
                // onClick={() => toggleFullNote(i)}
              >
                {expandedNotes.has(i) ? "Hide full Note" : "See full Note"}
              </Typography>
            </Box>
          </Box>
        ))}
      </Collapse>
    </>
  );
}
